import { useQuery } from "react-query";

import axios, { AxiosError } from "axios";

import { CLIENT_SYNC_SVC_ADDRESS } from "../../../constants/app.constants";

// ─── Types ───────────────────────────────────────────────────────────────────

export type SchemaStatusSyncType =
  | "production"
  | "forecast"
  | "columns"
  | "sticks"
  | "performance";

export interface SyncTypeStatus {
  readonly syncType: SchemaStatusSyncType;
  readonly lastSyncedAt: string;
}

export interface SchemaStatusResponse {
  readonly schemaId: string;
  readonly statuses: readonly SyncTypeStatus[];
}

export interface UseSchemaStatusProps {
  /** The schema key to fetch status for (e.g. `acme_valnav`). Pass `null` to skip fetching. */
  readonly schema: string | null;
  /** Defaults to `true`. Set to `false` to suppress the query without passing `null` as the schema. */
  readonly enabled?: boolean;
}

// ─── Query key ───────────────────────────────────────────────────────────────

export const SCHEMA_STATUS_QUERY_KEY = "schemaStatus" as const;

export const buildSchemaStatusQueryKey = (schema: string | null) =>
  [SCHEMA_STATUS_QUERY_KEY, schema] as const;

// ─── API ─────────────────────────────────────────────────────────────────────

const buildSchemaStatusPath = (schema: string): string =>
  `${CLIENT_SYNC_SVC_ADDRESS}/schema/${encodeURIComponent(schema)}/status`;

const fetchSchemaStatus = async (schema: string): Promise<SchemaStatusResponse> => {
  const response = await axios.get<SchemaStatusResponse>(buildSchemaStatusPath(schema));
  return response.data;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Fetches the latest completed sync status for each syncType under the given
 * schema from `GET /schema/{schema_id}/status` on sync-svc.
 *
 * The query re-executes automatically whenever `schema` changes — pass `null`
 * or set `enabled: false` to suppress it entirely.
 *
 * @example
 * const { data, isLoading, isError } = useSchemaStatus({ schema: currentSchema });
 */
export const useSchemaStatus = ({ schema, enabled = true }: UseSchemaStatusProps) => {
  return useQuery<SchemaStatusResponse, AxiosError>(
    buildSchemaStatusQueryKey(schema),
    // `enabled` ensures schema is non-null and non-empty before this runs
    () => fetchSchemaStatus(schema as string),
    {
      enabled: enabled && schema !== null && schema.length > 0,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000 // 5 minutes — schema status is stable between syncs
    }
  );
};
